import { useCallback, useEffect, useState } from "react";

const KEY = "solstack-theme";

const read = () => {
  try {
    return localStorage.getItem(KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
};

const apply = (theme) => {
  // Explicit data-theme for both states — removing the attribute for
  // "dark" only worked by coincidence when the device's own system
  // preference also happened to be dark (theme.css falls back to the
  // light :root otherwise), which is why the toggle looked broken on
  // devices/browsers with a light system preference.
  document.documentElement.setAttribute("data-theme", theme === "light" ? "light" : "dark");
};

/**
 * Site-wide light/dark theme. Dark is the default; the choice is stored in
 * localStorage and reflected on <html data-theme>. An inline script in
 * index.html applies the stored value before first paint.
 */
export function useTheme() {
  const [theme, setThemeState] = useState(read);

  useEffect(() => {
    apply(theme);
  }, [theme]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY) setThemeState(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setTheme = useCallback((next) => {
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* ignore */
    }
    setThemeState(next);
  }, []);

  const toggle = useCallback(
    () => setTheme(read() === "light" ? "dark" : "light"),
    [setTheme]
  );

  return { theme, setTheme, toggle };
}
